/**
 * The Computer page (slice 6c §5, §7.4; boards 95–95f): the crew's computer,
 * live. It opens watching, view only, and holds nothing; **Take over** holds
 * it (waking it first when it's asleep) and gives the desktop in control,
 * and **Done** lets it go, back to watching. Leaving the page lets it go
 * too. There's no Stop: Stop belongs to a reply, in its chat, which the
 * page names and links to while the crew works.
 */
import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@crew/ui/components/button";
import { Link } from "@crew/ui/components/link";
import { PageTitle } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import {
  computerStateKey,
  releaseHold,
  releaseOnLeave,
  takeOver,
  useComputerState,
  type ComputerState,
  type DesktopLink,
} from "./api";
import {
  actionOf,
  asleepSince,
  footRight,
  heldUntil,
  holdsIt,
  pageHold,
  noteOf,
  onPhone,
  type Note as NoteLine,
  pageState,
  pillOf,
  screenOf,
  stepState,
  stillWords,
  WAKE_STEPS,
  type ActionKind,
  type NoteIcon,
  type PageState,
} from "./computer-view";
import {
  useControlLink,
  useKeepHold,
  useLayoutAtOpen,
  useWatchLink,
  type FirstLink,
} from "./desktop-link";
import { PhoneStream, Stream } from "./desktop-sheet";
import { ClockIcon, DesktopPill, EyeIcon, LockIcon, MoonIcon, PhoneIcon } from "./marks";

/** The take's own link, so the desktop doesn't ask for a second; none while it wakes. */
const firstOf = (r: DesktopLink | { waking: true }): FirstLink | null => ("url" in r ? r : null);

/** Take over and Done (§5), with what they answered: the take's link, or why not. */
function useHoldActions() {
  const client = useQueryClient();
  const [first, setFirst] = useState<FirstLink | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Left the page while a Take over was out: a hold it gets is let go (Codex on #375).
  const gone = useRef(false);
  useEffect(() => {
    gone.current = false;
    return () => {
      gone.current = true;
    };
  }, []);
  const refresh = () => client.invalidateQueries({ queryKey: computerStateKey });
  const failed = async (e: unknown) => {
    setError(errorHint(e));
    await refresh();
  };
  const take = useMutation({
    mutationFn: takeOver,
    onMutate: () => setError(null),
    onSuccess: async (r) => {
      if (gone.current) return releaseOnLeave();
      setFirst(firstOf(r));
      await refresh();
    },
    // A take can fail after its hold committed (no link, say): let it go if the page left.
    onError: (e) => (gone.current ? releaseOnLeave() : failed(e)),
  });
  const release = useMutation({
    mutationFn: releaseHold,
    onMutate: () => setError(null),
    onSuccess: async () => {
      setFirst(null);
      await refresh();
    },
    onError: failed,
  });
  const busy = take.isPending || release.isPending;
  return {
    first,
    /** The take's link is used: a later mount (after a recovery, say) asks for a fresh one. */
    used: () => setFirst(null),
    error,
    busy,
    /** A Take over is out: its own answer brings the first link (Codex on #375). */
    taking: take.isPending,
    take: () => take.mutate(),
    release: () => release.mutate(),
  };
}

type Hold = ReturnType<typeof useHoldActions>;

/** Leaving the page, or closing the tab, lets the hold go (§5); the 15 minutes catch the rest. */
function useReleaseOnLeave(holding: boolean) {
  const held = useRef(holding);
  useEffect(() => {
    held.current = holding;
  });
  useEffect(() => {
    const leave = () => {
      if (held.current) releaseOnLeave();
    };
    addEventListener("pagehide", leave);
    return () => {
      removeEventListener("pagehide", leave);
      leave();
    };
  }, []);
}

/**
 * The page's hold over its life (§5): kept while it's yours, let go on leaving
 * or when it's no use, and the state read again when someone's hold runs out.
 */
function useHoldUpkeep(data: ComputerState | undefined, s: PageState, hold: Hold, me: string) {
  const mine = pageHold(data, me);
  useKeepHold(holdsIt(s));
  useReleaseOnLeave(mine || hold.taking);
  useReleaseUnusable(s.kind === "unavailable" && mine, hold);
  useRefetchAt(heldUntil(data));
}

/** A hold on a computer whose wake failed is no use to anyone: the page lets it go. */
function useReleaseUnusable(unusable: boolean, hold: Hold) {
  const release = useEffectEvent(() => hold.release());
  useEffect(() => {
    if (unusable) release();
  }, [unusable]);
}

/** Someone's hold ends at `until` with no event: the page reads the state again then. */
function useRefetchAt(until: string | null) {
  const client = useQueryClient();
  const refetch = useEffectEvent(
    () => void client.invalidateQueries({ queryKey: computerStateKey }),
  );
  useEffect(() => {
    if (!until) return;
    const timer = setTimeout(refetch, Math.max(0, new Date(until).getTime() - Date.now()) + 500);
    return () => clearTimeout(timer);
  }, [until]);
}

const Pill = ({ s }: { s: PageState }) => {
  const pill = pillOf(s);
  return (
    <DesktopPill dot={pill.dot} moss={pill.moss}>
      {pill.text}
    </DesktopPill>
  );
};

function ActionButton({ action, hold, phone }: { action: ActionKind; hold: Hold; phone: boolean }) {
  return (
    <Button
      data-computer-action
      variant={action.outline ? "outline" : "default"}
      onClick={hold[action.does]}
      disabled={action.disabled || hold.busy}
      className={cn(
        "font-medium disabled:bg-foreground/[0.08] disabled:text-hint disabled:opacity-100",
        phone ? "h-[50px] w-full rounded-[10px] text-base" : "h-9 px-[18px] text-sm",
      )}
    >
      {action.label}
    </Button>
  );
}

type Box = { phone: boolean; className: string };

/** The live desktop, view only: the agent's work, or nobody's (§4). */
function WatchDesktop({ phone, className }: Box) {
  const { link, again } = useWatchLink(true);
  if (phone) return <PhoneStream link={link} again={again} />;
  return <Stream link={link} again={again} className={className} />;
}

/** The live desktop in control, for the person holding it. */
function ControlDesktop({
  first,
  used,
  phone,
  className,
}: Box & { first: FirstLink | null; used: () => void }) {
  const { link, again } = useControlLink(true, first);
  // Take over's link opens one stream, once: past its 10 minutes it can't
  // reconnect, so a remount never reuses it (Codex on #375).
  const ready = link.state === "ready";
  useEffect(() => {
    if (ready && first) used();
  }, [ready, first, used]);
  if (phone) return <PhoneStream link={link} again={again} control />;
  return <Stream link={link} again={again} control className={className} />;
}

/**
 * The hold's event can come before the take's answer: its link is on the
 * way, so no second one is asked for (Codex on #375).
 */
const HeldDesktop = ({ hold, ...box }: Box & { hold: Hold }) =>
  hold.taking ? (
    <Connecting {...box} />
  ) : (
    <ControlDesktop first={hold.first} used={hold.used} {...box} />
  );

/** The stream's loading frame while Take over's answer, and its link, are on the way. */
function Connecting({ phone, className }: Box) {
  const link = { state: "loading" } as const;
  if (phone) return <PhoneStream link={link} again={() => {}} control />;
  return <Stream link={link} again={() => {}} control className={className} />;
}

/** No stream: asleep, waking, someone else has it, or nothing to show (95a, 95b). */
function Still({
  s,
  since,
  className,
  takeHere,
}: {
  s: PageState;
  since: string | null;
  className: string;
  takeHere: boolean;
}) {
  const words = stillWords(s, since, takeHere);
  return (
    <div
      data-computer-still
      className={cn(
        "flex flex-col items-center justify-center gap-[22px] bg-[#1B2232] px-6 text-center",
        className,
      )}
    >
      {s.kind === "asleep" ? <MoonIcon size={40} stroke="#7D8699" strokeWidth="1.5" /> : null}
      {s.kind === "waking" ? <WakeSpinner /> : null}
      <div className="flex flex-col items-center gap-2">
        <p className="text-question leading-[22px] font-medium text-[#E6E9F0]">{words.title}</p>
        <p className="text-sm leading-[18px] text-[#9AA3B5]">{words.line}</p>
      </div>
      {s.kind === "waking" ? <WakeSteps step={s.step} /> : null}
    </div>
  );
}

const WakeSpinner = () => (
  <svg width="40" height="40" viewBox="0 0 40 40" aria-hidden className="animate-spin">
    <circle cx="20" cy="20" r="16" fill="none" stroke="#2C3446" strokeWidth="3" />
    <path
      d="M20 4a16 16 0 0 1 16 16"
      fill="none"
      stroke="#8FB59C"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);

const STEP_TEXT = { done: "text-[#C9CEDA]", now: "text-[#E6E9F0]", later: "text-[#7D8699]" };

/** The wake's steps as they pass (95b): done, on it, still to come. */
function WakeSteps({ step }: { step: number }) {
  return (
    <ol data-wake-steps className="flex flex-col gap-2.5 pt-1.5 text-left">
      {WAKE_STEPS.map((label, i) => (
        <WakeStep key={label} label={label} at={stepState(i, step)} />
      ))}
    </ol>
  );
}

function WakeStep({ label, at }: { label: string; at: ReturnType<typeof stepState> }) {
  return (
    <li className={cn("flex items-center gap-2.5 text-meta leading-4", STEP_TEXT[at])}>
      {at === "done" ? (
        <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden className="shrink-0">
          <path d="M20 6 9 17l-5-5" fill="none" stroke="#8FB59C" strokeWidth="2.4" />
        </svg>
      ) : (
        <span className="flex size-3.5 shrink-0 items-center justify-center">
          <span
            className={cn("size-1.5 rounded-full", at === "now" ? "bg-[#8FB59C]" : "bg-[#4A5266]")}
          />
        </span>
      )}
      {label}
    </li>
  );
}

const WIDE_BOX =
  "aspect-video w-[min(100%,1112px,calc((100dvh-15rem)*16/9))] shrink-0 overflow-clip rounded-[10px] shadow-[0_0_0_1px_var(--color-border)]";

/** What the desktop area shows: the stream (in control or view only), or a still. */
function Screen({ s, hold, since, phone, phoneNote }: Parts & { phone: boolean }) {
  const box = { phone, className: phone ? "min-h-0 flex-1" : WIDE_BOX };
  const screen = screenOf(s);
  if (screen === "control") return <HeldDesktop hold={hold} {...box} />;
  if (screen === "watch") return <WatchDesktop {...box} />;
  return <Still s={s} since={since} className={box.className} takeHere={!phoneNote} />;
}

const ICONS: Record<NoteIcon, typeof EyeIcon> = {
  eye: EyeIcon,
  moon: MoonIcon,
  clock: ClockIcon,
  lock: LockIcon,
};

/** The footer's line (95–95f): what the state means, and Open the chat → while it's another's. */
function Note({ note, phone }: { note: NoteLine | null; phone: boolean }) {
  if (!note) return null;
  const Icon = ICONS[note.icon];
  return (
    <>
      <Icon size={14} className={phone ? "mt-[3px]" : undefined} />
      <span className="min-w-0">{note.text}</span>
      {note.chat ? (
        <Link
          href={`/chats/${note.chat}`}
          className="shrink-0 self-end font-medium text-foreground underline underline-offset-2"
        >
          Open the chat →
        </Link>
      ) : null}
    </>
  );
}

/** The footer's right side: an error, or the state in a few words. */
function FootRight({ error, text }: { error: string | null; text: string | null }) {
  if (error) {
    return (
      <span role="alert" className="ml-auto shrink-0 text-brick">
        {error}
      </span>
    );
  }
  return text ? <span className="ml-auto shrink-0 text-hint">{text}</span> : null;
}

type Parts = {
  s: PageState;
  hold: Hold;
  since: string | null;
  action: ActionKind | null;
  /** A phone's line in place of the state's, where Take over isn't done on a phone (S-455). */
  phoneNote?: NoteLine | null;
};

/** Boards 95–95c: the header with the pill and the action, the desktop at the page's width, the footer. */
function WidePage(parts: Parts) {
  const { s, hold, since, action } = parts;
  return (
    <main data-computer-page={s.kind} className="flex min-w-0 flex-col gap-[18px] px-12 py-7">
      <header className="flex items-end justify-between gap-6">
        <div className="flex flex-col gap-1">
          <Link href="/settings/tools" className="text-meta leading-4 text-muted-foreground">
            ← Settings · Tools
          </Link>
          <PageTitle className="leading-[46px]">Computer</PageTitle>
          <p className="text-sm leading-[18px] text-copy">
            Your crew's own computer. Sign in to a site here, and the crew can use it after.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-4 pb-0.5">
          <Pill s={s} />
          {action ? <ActionButton action={action} hold={hold} phone={false} /> : null}
        </div>
      </header>
      <Screen {...parts} phone={false} />
      <div
        data-computer-foot
        className="flex w-[min(100%,1112px)] items-center gap-2.5 text-meta leading-4 text-muted-foreground"
      >
        <Note note={noteOf(s, false)} phone={false} />
        <FootRight error={hold.error} text={footRight(s, since)} />
      </div>
    </main>
  );
}

/**
 * Boards 95d–95f: over the tab bar, under the top bar, as a thread is; the
 * desktop zoomed on the page with Fit/Zoom, and the action full width at
 * the bottom.
 */
function PhonePage(parts: Parts) {
  const { s, hold, action } = parts;
  return (
    <main
      data-computer-page={s.kind}
      className="fixed inset-x-0 top-[calc(3.5rem+env(safe-area-inset-top))] bottom-0 z-40 flex flex-col bg-background"
    >
      <div className="flex flex-col gap-1.5 px-[18px] py-3.5">
        <Link href="/settings/tools" className="text-meta leading-4 text-muted-foreground">
          ← Settings
        </Link>
        <div className="flex items-center justify-between gap-3">
          <h1 className="font-display text-[34px] leading-10 text-foreground">Computer</h1>
          <Pill s={s} />
        </div>
        <p className="text-sm leading-5 text-copy">
          Sign in to a site here, and the crew can use it after.
        </p>
      </div>
      <Screen {...parts} phone />
      <PhoneHint s={s} />
      <div className="flex flex-col gap-3.5 border-t border-border px-[18px] pt-4 pb-[max(env(safe-area-inset-bottom),34px)]">
        <div
          data-computer-foot
          className="flex items-start gap-2.5 text-meta leading-[19px] text-muted-foreground empty:hidden"
        >
          <Note note={parts.phoneNote ?? noteOf(s, true)} phone />
        </div>
        <FootRight error={hold.error} text={null} />
        {action ? <ActionButton action={action} hold={hold} phone /> : null}
      </div>
    </main>
  );
}

/** Under a desktop you can look around (95d, 95e): Fit and Zoom. */
function PhoneHint({ s }: { s: PageState }) {
  if (s.kind !== "watching" && s.kind !== "yours") return null;
  return (
    <div className="flex items-center gap-2.5 px-[18px] py-3.5 text-sm leading-5 text-muted-foreground">
      <PhoneIcon size={18} />
      Opens zoomed on the page. Fit shows the whole screen; sideways is bigger.
    </div>
  );
}

export function ComputerPage({ me }: { me: Me }) {
  // The layout it opened with, so turning a phone keeps the stream's iframe (Codex on #373).
  const wide = useLayoutAtOpen(true);
  const state = useComputerState();
  const s = pageState(state.data, me.user.id);
  const hold = useHoldActions();
  useHoldUpkeep(state.data, s, hold, me.user.id);
  if (state.isPending) return null;
  const parts = {
    s,
    hold,
    since: asleepSince(state.data, me.workspace.timezone),
    action: actionOf(s),
  };
  if (wide) return <WidePage {...parts} />;
  const phone = onPhone(parts.action, state.data?.phone_control);
  return <PhonePage {...parts} action={phone.action} phoneNote={phone.note} />;
}
